import { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp, TrendingDown, Minus, Users, Clock, DollarSign,
  Building2, Loader2, ArrowRight, AlertTriangle, UserPlus, UserMinus,
  BarChart3, ChevronDown, ChevronRight,
} from "lucide-react";
import { Button } from "./components/ui/button";

import { API } from "./apiConfig";

export function ComparisonDashboard() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [currentId, setCurrentId] = useState("");
  const [previousId, setPreviousId] = useState("");
  const [expandedDepts, setExpandedDepts] = useState(new Set());

  useEffect(() => {
    axios.get(`${API}/reports/compare`, { withCredentials: true })
      .then((r) => {
        const reps = r.data.reports || [];
        setReports(reps);
        if (reps.length >= 2) {
          setCurrentId(reps[0].file_id);
          setPreviousId(reps[1].file_id);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCompare = async () => {
    if (!currentId || !previousId || currentId === previousId) return;
    setComparing(true);
    try {
      const { data } = await axios.post(`${API}/reports/compare-pair`, {
        current_id: currentId, previous_id: previousId,
      }, { withCredentials: true });
      setComparison(data);
    } catch {
      setComparison(null);
    } finally {
      setComparing(false);
    }
  };

  useEffect(() => {
    if (currentId && previousId && currentId !== previousId) handleCompare();
  }, [currentId, previousId]);

  const toggleDept = (dept) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      next.has(dept) ? next.delete(dept) : next.add(dept);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="comparison-loading">
        <Loader2 className="animate-spin text-[#0A1F3B]" size={24} />
      </div>
    );
  }

  if (reports.length < 2) {
    return (
      <div className="text-center py-16" data-testid="comparison-empty">
        <BarChart3 size={40} className="text-slate-500 mx-auto mb-3" />
        <h3 className="font-heading text-lg font-bold text-[#0A1F3B] mb-2">Not Enough Data Yet</h3>
        <p className="text-sm text-slate-600 max-w-sm mx-auto">
          Generate at least 2 Excel reports to unlock period-over-period comparison. Each report captures labor costs, hours, and department breakdowns.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="comparison-dashboard">
      {/* Report Selector */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg" data-testid="report-selector">
        <div className="flex-1 min-w-[180px]">
          <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Previous Period</label>
          <select
            value={previousId}
            onChange={(e) => setPreviousId(e.target.value)}
            className="w-full h-8 text-xs bg-slate-50 border border-slate-200 rounded px-2 text-[#0A1F3B] focus:border-[#0A1F3B] focus:outline-none"
            data-testid="previous-report-select"
          >
            {reports.map((r) => (
              <option key={r.file_id} value={r.file_id} disabled={r.file_id === currentId}>
                {r.date_range || r.file_name} ({new Date(r.created_at).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
        <ArrowRight size={16} className="text-slate-500 mt-4 hidden sm:block" />
        <div className="flex-1 min-w-[180px]">
          <label className="text-[9px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Current Period</label>
          <select
            value={currentId}
            onChange={(e) => setCurrentId(e.target.value)}
            className="w-full h-8 text-xs bg-slate-50 border border-slate-200 rounded px-2 text-[#0A1F3B] focus:border-[#0A1F3B] focus:outline-none"
            data-testid="current-report-select"
          >
            {reports.map((r) => (
              <option key={r.file_id} value={r.file_id} disabled={r.file_id === previousId}>
                {r.date_range || r.file_name} ({new Date(r.created_at).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      </div>

      {comparing && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#0A1F3B]" size={24} />
        </div>
      )}

      {comparison && !comparing && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="comparison-summary">
            <ComparisonCard
              label="Total Labor Cost"
              current={comparison.summary.current_total_cost}
              previous={comparison.summary.previous_total_cost}
              prefix="$"
              data-testid="compare-total-cost"
            />
            <ComparisonCard
              label="Overtime Cost"
              current={comparison.summary.current_ot_cost}
              previous={comparison.summary.previous_ot_cost}
              prefix="$"
              invertColor
              data-testid="compare-ot-cost"
            />
            <ComparisonCard
              label="Headcount"
              current={comparison.summary.current_employee_count}
              previous={comparison.summary.previous_employee_count}
              data-testid="compare-headcount"
            />
            <ComparisonCard
              label="Total Shifts"
              current={comparison.summary.current_total_shifts}
              previous={comparison.summary.previous_total_shifts}
              data-testid="compare-shifts"
            />
          </div>

          {/* Department Changes */}
          {comparison.department_changes.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="dept-changes">
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
                <Building2 size={14} className="text-[#0A1F3B]" />
                <span className="text-xs font-bold text-[#0A1F3B]">Department Breakdown</span>
              </div>
              <div className="divide-y divide-[#0A1F3B]/50">
                {comparison.department_changes.map((d) => {
                  const pct = d.previous_cost > 0
                    ? ((d.current_cost - d.previous_cost) / d.previous_cost * 100).toFixed(1)
                    : d.current_cost > 0 ? "new" : "0";
                  const isExpanded = expandedDepts.has(d.department);
                  const deptEmployees = comparison.employee_changes.filter(
                    (e) => e.department === d.department
                  );
                  return (
                    <div key={d.department}>
                      <div
                        className="flex items-center justify-between px-4 py-2 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => toggleDept(d.department)}
                        data-testid={`dept-row-${d.department.replace(/\s/g, '-')}`}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={12} className="text-slate-500" /> : <ChevronRight size={12} className="text-slate-500" />}
                          <span className="text-xs font-semibold text-[#0A1F3B]">{d.department}</span>
                          <span className="text-[10px] text-slate-500">{d.current_employees} emp</span>
                        </div>
                        <div className="flex items-center gap-4 text-[10px] font-mono">
                          <span className="text-slate-500">${d.previous_cost.toFixed(2)}</span>
                          <ArrowRight size={10} className="text-slate-500" />
                          <span className="text-[#0A1F3B]">${d.current_cost.toFixed(2)}</span>
                          <ChangeIndicator value={d.cost_change} prefix="$" />
                        </div>
                      </div>
                      {isExpanded && deptEmployees.length > 0 && (
                        <div className="bg-slate-50/50 px-4 py-1">
                          {deptEmployees.map((emp) => (
                            <div key={emp.name} className="flex items-center justify-between py-1 text-[10px]">
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-600">{emp.name}</span>
                                {emp.is_new && <span className="text-[8px] bg-green-500/20 text-green-700 px-1 rounded">NEW</span>}
                                {emp.is_removed && <span className="text-[8px] bg-red-500/20 text-red-700 px-1 rounded">LEFT</span>}
                              </div>
                              <div className="flex items-center gap-3 font-mono">
                                <span className="text-slate-500">{emp.previous_hours.toFixed(2)}h</span>
                                <ArrowRight size={8} className="text-slate-500" />
                                <span className="text-[#0A1F3B]">{emp.current_hours.toFixed(2)}h</span>
                                <ChangeIndicator value={emp.cost_change} prefix="$" size="sm" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Employee Changes Table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="employee-changes">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
              <Users size={14} className="text-[#0A1F3B]" />
              <span className="text-xs font-bold text-[#0A1F3B]">Employee Changes</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="text-left px-3 py-2">Employee</th>
                    <th className="text-left px-3 py-2">Department</th>
                    <th className="text-right px-3 py-2">Prev Hours</th>
                    <th className="text-right px-3 py-2">Curr Hours</th>
                    <th className="text-right px-3 py-2">Hours Chg</th>
                    <th className="text-right px-3 py-2">Prev Cost</th>
                    <th className="text-right px-3 py-2">Curr Cost</th>
                    <th className="text-right px-3 py-2">Cost Chg</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.employee_changes.map((emp) => (
                    <tr
                      key={emp.name}
                      className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${
                        emp.is_new ? "bg-green-500/5" : emp.is_removed ? "bg-red-500/5 opacity-60" : ""
                      }`}
                      data-testid={`compare-emp-${emp.name.replace(/\s/g, '-')}`}
                    >
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-[#0A1F3B]">{emp.name}</span>
                          {emp.is_new && <UserPlus size={10} className="text-green-700" />}
                          {emp.is_removed && <UserMinus size={10} className="text-red-700" />}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-slate-600">{emp.department}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-500">{emp.previous_hours.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[#0A1F3B]">{emp.current_hours.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <ChangeIndicator value={emp.hours_change} suffix="h" />
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-500">
                        {emp.previous_cost > 0 ? `$${emp.previous_cost.toFixed(2)}` : "-"}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-[#0A1F3B]">
                        {emp.current_cost > 0 ? `$${emp.current_cost.toFixed(2)}` : "-"}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <ChangeIndicator value={emp.cost_change} prefix="$" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ComparisonCard({ label, current, previous, prefix = "", invertColor = false, ...rest }) {
  const change = current - previous;
  const pct = previous > 0 ? ((change / previous) * 100).toFixed(1) : current > 0 ? 100 : 0;
  const isUp = change > 0;
  const isDown = change < 0;
  const changeColor = invertColor
    ? (isUp ? "text-red-700" : isDown ? "text-green-700" : "text-slate-500")
    : (isUp ? "text-green-700" : isDown ? "text-red-700" : "text-slate-500");

  return (
    <div className="p-3 bg-white border border-slate-200 rounded-lg" data-testid={rest["data-testid"]}>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
      <p className="text-lg font-mono font-bold text-[#0A1F3B] mt-1">
        {prefix}{typeof current === "number" ? current.toFixed(2) : current}
      </p>
      <div className="flex items-center gap-1.5 mt-1">
        {isUp ? <TrendingUp size={12} className={changeColor} /> :
         isDown ? <TrendingDown size={12} className={changeColor} /> :
         <Minus size={12} className="text-slate-500" />}
        <span className={`text-[10px] font-mono font-semibold ${changeColor}`}>
          {change >= 0 ? "+" : ""}{prefix}{change.toFixed(2)} ({pct}%)
        </span>
      </div>
      <span className="text-[9px] text-slate-500">vs {prefix}{typeof previous === "number" ? previous.toFixed(2) : previous}</span>
    </div>
  );
}

function ChangeIndicator({ value, prefix = "", suffix = "", size = "md" }) {
  const isUp = value > 0;
  const isDown = value < 0;
  const color = isUp ? "text-green-700" : isDown ? "text-red-700" : "text-slate-500";
  const textSize = size === "sm" ? "text-[9px]" : "text-[10px]";

  return (
    <span className={`font-mono font-semibold ${color} ${textSize}`}>
      {value >= 0 ? "+" : ""}{prefix}{Math.abs(value).toFixed(2)}{suffix}
    </span>
  );
}
